import type { FaceKey, FacePhotoSize } from '@repo/api-contracts';
import { Copy } from 'lucide-react';
import { type ReactElement, useState } from 'react';
import { useStore } from 'zustand';
import { useEditAreaDraftStore } from '@/entities/edit-area';
import { Button } from '@/shared/ui';
import { applyEditAreaCommand } from '../model/apply-edit-area-command';
import { FACE_LABELS } from '../model/face-labels';

const isSamePhotoSize = (a: FacePhotoSize | undefined, b: FacePhotoSize | undefined): boolean =>
  a !== undefined && b !== undefined && a.widthPx === b.widthPx && a.heightPx === b.heightPx;

/**
 * 사이드바 맨 아래 칸 — 다른 면의 배치를 이 면에 같은 mm 위치로 가져온다.
 * 앞면·뒷면 인쇄 영역을 똑같이 맞추는 가장 빠른 길이라 눈에 띄게 둔다.
 * 기준 사진 색상의 두 면 사진 px 크기가 같으면 추출 결과(윤곽·틀)도 함께 가져올 수 있다(기본 켜짐) — 같은 사진이면
 * px 단위로 똑같아진다. 사진 크기가 다르면 윤곽이 맞지 않으므로 배치만 가져온다. 배치가 있는 다른 면이 없으면 그리지 않는다.
 */
export const CopyFacePanel = (): ReactElement | null => {
  const store = useEditAreaDraftStore();
  const state = useStore(store);
  const [chosen, setChosen] = useState<FaceKey | null>(null);
  const [includeSurface, setIncludeSurface] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const target = state.selection.face;
  const sources = state.draft.activeFaces.filter(
    (face) => face !== target && (state.faces[face]?.placements.length ?? 0) > 0,
  );
  const source = sources.find((face) => face === chosen) ?? sources[0];
  if (source === undefined) return null;
  const disabled = state.locked || state.isUnsupported;
  const existing = state.faces[target]?.placements.length ?? 0;
  const photos = state.draft.colors.find(
    (color) => color.colorOptionId === state.selection.colorOptionId,
  )?.faces;
  const canCopySurface = isSamePhotoSize(photos?.[source]?.photoSize, photos?.[target]?.photoSize);
  const copySurface = canCopySurface && includeSurface;
  return (
    <section
      className="space-y-3 rounded-lg border-2 border-primary/60 bg-primary/5 p-4"
      aria-label="다른 면과 똑같이 맞추기"
    >
      <h2 className="flex items-center gap-2 font-semibold">
        <Copy aria-hidden="true" className="size-4" />
        다른 면과 똑같이 맞추기
      </h2>
      <p className="text-sm text-muted-foreground">
        <span className="block">다른 면의 배치를 같은 위치로 가져옵니다.</span>
        <span className="block">사진 구도·크기가 같아야 정확히 맞습니다.</span>
      </p>
      <div className="flex flex-wrap items-end gap-2">
        <label className="grid gap-1 text-sm">
          가져올 면
          <select
            className="rounded border bg-background p-2"
            value={source}
            disabled={disabled}
            onChange={(event) => {
              const next = sources.find((face) => face === event.target.value);
              if (next === undefined) return;
              setError(null);
              setChosen(next);
            }}
          >
            {sources.map((face) => (
              <option key={face} value={face}>
                {FACE_LABELS[face]}
              </option>
            ))}
          </select>
        </label>
        <Button
          type="button"
          disabled={disabled}
          onClick={() => {
            if (
              existing > 0 &&
              !window.confirm(
                `이 면의 배치 ${existing}개를 ${FACE_LABELS[source]}에서 가져온 배치로 바꾸시겠습니까?${copySurface ? ' 윤곽·틀도 함께 바뀝니다.' : ''}`,
              )
            )
              return;
            setError(
              applyEditAreaCommand(store, () =>
                state.copyFace(source, target, { includeSurface: copySurface }),
              ),
            );
          }}
        >
          같은 위치로 가져오기
        </Button>
      </div>
      <label className="flex items-center gap-2 text-sm font-medium">
        <input
          type="checkbox"
          className="size-4"
          checked={copySurface}
          disabled={disabled || !canCopySurface}
          onChange={(event) => {
            setError(null);
            setIncludeSurface(event.target.checked);
          }}
        />
        윤곽·틀도 함께 가져오기
      </label>
      <p className="text-xs text-muted-foreground">
        {canCopySurface
          ? '추출 결과(윤곽·틀)까지 복사하면 같은 사진을 쓰는 두 면이 px 단위로 똑같아집니다.'
          : '두 면의 사진 크기가 달라 윤곽·틀은 가져올 수 없습니다. 배치만 가져옵니다.'}
      </p>
      {error ? (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      ) : null}
    </section>
  );
};
